import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatChange, formatNumber } from "../../../../utils/numbers";
import { useActiveUsersEvolution } from "./useDashboardData";

const PERIODS = [7, 30, 90];

/** Direction of an evolution in %: "up", "down" or "flat". */
export const trendOf = (change) =>
  change > 0 ? "up" : change < 0 ? "down" : "flat";

/** Evolution chip: "▲ +12 %" in green, "▼ −5 %" in red. */
export const TrendChip = ({ change, title }) => {
  const label = formatChange(change);
  if (label === null) return null;
  const trend = trendOf(change);
  const icon = {
    up: "ri-arrow-up-line",
    down: "ri-arrow-down-line",
    flat: "ri-subtract-line",
  }[trend];
  return (
    <span className={`dash-trend dash-trend-${trend}`} title={title}>
      <i className={icon} aria-hidden="true"></i>
      {label}
    </span>
  );
};

/** Active users over a period with the evolution vs the previous period of the same length. */
const ActiveUsersTrend = () => {
  const { t, i18n } = useTranslation();
  const [days, setDays] = useState(30);
  const { loading, data } = useActiveUsersEvolution(days);

  const previousLabel = t("vs {{previous}} the previous {{days}} days", {
    previous: formatNumber(data?.previous ?? 0),
    days,
  });

  return (
    <div className="dash-active-trend">
      <div className="dash-active-trend-header">
        <p className="dash-split-title">{t("Active users")}</p>
        <select
          className="dash-select"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          aria-label={t("Period")}
        >
          {PERIODS.map((value) => (
            <option value={value} key={value}>
              {t("Last {{days}} days", { days: value })}
            </option>
          ))}
        </select>
      </div>

      {loading && !data ? (
        <div className="dash-skeleton" style={{ height: 48 }} />
      ) : !data ? (
        <p className="dash-active-trend-note">{t("Not available")}</p>
      ) : (
        <>
          <div className="dash-active-trend-value">
            <span>{formatNumber(data.current)}</span>
            {data.complete && <TrendChip change={data.change} />}
          </div>
          <p className="dash-active-trend-note">
            {!data.complete
              ? t("Not enough history to compare yet (tracked since {{date}})", {
                  date: data.trackedSince
                    ? new Date(data.trackedSince).toLocaleDateString(i18n.language)
                    : "—",
                })
              : data.change === null
              ? t("No active users the previous {{days}} days", { days })
              : previousLabel}
          </p>
        </>
      )}
    </div>
  );
};

export default ActiveUsersTrend;
